import React, { memo } from 'react';
import { Box, Flex, Heading, SimpleGrid, Stack, Alert, AlertIcon } from '@chakra-ui/react';
import ProductCard from './ProductCard';
import { motion } from 'framer-motion';
import CategorySectionSkeleton from './CategorySectionSkeleton';

const MotionSimpleGrid = motion(SimpleGrid);
const MotionFlex = motion(Flex);

const CategorySection = memo(({ products, loading, error }) => {
  if (loading) {
    return <CategorySectionSkeleton />;
  }

  if (error) {
    return (
      <Box py={12} px={{ base: 4, md: 8, lg: 16 }} bg="gray.50">
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box py={{ base: 12, md: 16 }} px={{ base: 4, md: 8, lg: 16 }}>
      <Stack spacing={8}>
        <Heading 
          as="h2" 
          textAlign="center" 
          fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }} 
          mb={{ base: 6, md: 8 }}
          fontFamily="'Cormorant', serif"
          fontWeight="700"
          letterSpacing="wide"
          color="gray.800"
        >
          Featured Products
        </Heading>
        <>
          {/* Desktop View */}
          <MotionSimpleGrid 
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }} 
            spacing={8}
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            display={{ base: 'none', md: 'grid' }}
          >
            {products.map(product => (
              <ProductCard 
                key={product.id} 
                product={{
                  id: product.id,
                  name: product.name,
                  category: product.productCategory.charAt(0).toUpperCase() + product.productCategory.slice(1), // Map productCategory to user-friendly text (e.g., 'ring' -> 'Rings')
                  price: parseFloat(product.price), // Ensure price is a number
                  primaryImage: product.primaryImage,
                  slug: product.slug,
                  stock: product.stock,
                  sizesAvailable: product.sizesAvailable, // New field for sizes availability
                  sizes: product.sizes || null,
                  stockDivision:product.stockDivision||null // New field for sizes, default to null if not available
                }} 
              />
            ))}
          </MotionSimpleGrid>

          {/* Mobile View */}
          <MotionFlex
            display={{ base: 'flex', md: 'none' }}
            overflowX="auto"
            whiteSpace="nowrap"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            sx={{
              '&::-webkit-scrollbar': { display: 'none' },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none',
            }} // Hide scrollbar
          >
            {products.map(product => (
              <Box key={product.id} minW={{ base: '280px', sm: '300px' }} mr={4} flexShrink={0} p={2}>
                <ProductCard 
                  product={{
                    id: product.id,
                    name: product.name,
                    category: product.productCategory.charAt(0).toUpperCase() + product.productCategory.slice(1) , // Map productCategory to user-friendly text (e.g., 'ring' -> 'Rings')
                    price: parseFloat(product.price), // Ensure price is a number
                    primaryImage: product.primaryImage,
                    slug: product.slug,
                    stock: product.stock,
                  }} 
                />
              </Box>
            ))}
          </MotionFlex>
        </>
      </Stack>
    </Box>
  );
});

export default CategorySection;